import { FC } from "react";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { NetworkPortfolioManagementMembership } from "@app/network-portfolio-management/interfaces";
import { formatCount, formatPercent, formatScore } from "@app/network-portfolio-management/utils/formatUtils";

interface AdequacyOverviewProps {
  membership: NetworkPortfolioManagementMembership | null;
  projectId: number;
}

const HELP_TEXT = (
  <ul className="network-portfolio-management-project-card__adequacy-overview-tooltip-list">
    <li>Adequacy Overview shows the count of county-specialty combinations within this project, broken out by their current adequacy status.</li>
    <li>Approved Exceptions is a subset of Total Gap Count - these are gaps that have been reviewed and formally excused and are not a separate category.</li>
  </ul>
);

type CountFieldKey = "adequacyMetCount" | "adequacyTotalGapCount" | "adequacyApprovedExceptionsCount";
type PercentFieldKey = "adequacyMetPercent" | "adequacyTotalGapPercent";

interface StatusKeyConfig {
  colorModifier: string;
  countKey: CountFieldKey;
  label: string;
  percentKey: PercentFieldKey | null;
}

// Fixed AC order and color treatment (colorModifier drives the SCSS `--adequate`/`--total-gaps`/etc.
// modifier classes) — do not reorder. Adequate and Total Gap Count are the disjoint pair summing to
// 100%; Approved Exceptions overlaps with Total Gap Count, so it's a plain count with no percent
// (see BuildAdequacyOverviewCounts).
const STATUS_KEYS: readonly StatusKeyConfig[] = [
  { colorModifier: "adequate", countKey: "adequacyMetCount", label: "Adequate County-Specialties", percentKey: "adequacyMetPercent" },
  { colorModifier: "total-gaps", countKey: "adequacyTotalGapCount", label: "Total Gap Count (Current Service Area)", percentKey: "adequacyTotalGapPercent" },
  { colorModifier: "approved-exceptions", countKey: "adequacyApprovedExceptionsCount", label: "Approved Exceptions", percentKey: null },
];

export const AdequacyOverview: FC<AdequacyOverviewProps> = ({ membership, projectId }) => (
  <section
    aria-labelledby={`adequacy-overview-section-label-${projectId}`}
    className="network-portfolio-management-project-card__adequacy-overview"
    data-testid={`network-portfolio-management-card-adequacy-overview-${projectId}`}
  >
    <div className="network-portfolio-management-project-card__adequacy-overview-header">
      <span
        className="network-portfolio-management-project-card__adequacy-overview-label"
        id={`adequacy-overview-section-label-${projectId}`}
      >
        Adequacy Overview
      </span>
      <TooltipWrapper position="top" text={HELP_TEXT} tooltipWidth="20rem">
        <div
          className="network-portfolio-management-project-card__adequacy-overview-info"
          data-testid={`network-portfolio-management-card-adequacy-overview-info-${projectId}`}
        >
          <QesIcon description="Info" iconName={QesIcons.InfoCircle} />
        </div>
      </TooltipWrapper>
    </div>
    <div className="network-portfolio-management-project-card__adequacy-overview-row">
      <div
        className="network-portfolio-management-project-card__adequacy-overview-cell"
        data-testid={`network-portfolio-management-card-adequacy-overview-cell-${projectId}-index-score`}
      >
        <span className="network-portfolio-management-project-card__adequacy-overview-status-label">Index Score</span>
        <span className="network-portfolio-management-project-card__adequacy-overview-num">{formatScore(membership?.adequacyScore)}</span>
      </div>
      {STATUS_KEYS.map(({ colorModifier, countKey, label, percentKey }) => {
        const count = membership?.[countKey] ?? null;
        const percent = percentKey != null ? membership?.[percentKey] ?? null : null;

        return (
          <div
            className="network-portfolio-management-project-card__adequacy-overview-cell"
            data-testid={`network-portfolio-management-card-adequacy-overview-cell-${projectId}-${colorModifier}`}
            key={label}
          >
            <span className="network-portfolio-management-project-card__adequacy-overview-status-label">{label}</span>
            <span className="network-portfolio-management-project-card__adequacy-overview-num">
              {formatCount(count)}
              {count != null && percent != null && (
                <span
                  className={`network-portfolio-management-project-card__adequacy-overview-percent network-portfolio-management-project-card__adequacy-overview-percent--${colorModifier}`}
                >
                  {` (${formatPercent(percent)})`}
                </span>
              )}
            </span>
          </div>
        );
      })}
    </div>
  </section>
);
